// Player store. Cross-component player state — currently-tuned channel,
// active mode (catchup / rewind / transcode / proxy). Exists specifically
// to satisfy the Phase 2.5 section C
// non-negotiable that bans `window.__nodecast*` (the legacy SPA hung the
// player instance on `window.app.player` and let any component reach into it).
//
// (Google Cast was removed in Atlas R5 — it didn't work reliably and isn't in
// the design; the CastManager/CastButton subsystem + this store's cast snapshot
// went with it.)
//
// State here is intentionally narrow. Everything that's a render concern
// (overlay visibility, hover state) stays in the component. Everything
// that's a CROSS-component fact (which channel are we tuned to) lives here.

import { defineStore } from 'pinia';
import { ref, computed, shallowRef } from 'vue';
import {
    useStreamUrl,
    type StreamMode,
    type StreamUrlResult,
    type CatchupRequest,
} from '@/composables/useStreamUrl';
import type { Channel } from '@/stores/channelList';
import { useSettingsStore } from '@/stores/settings';
import { useFiltersStore } from '@/stores/filters';
import { ApiError } from '@/lib/api';
import { log } from '@/lib/log';
import { createPlaybackAttemptId } from '@/lib/playbackAttemptId';
import { isStreamLimitError, resolveWithStreamLimitRetry } from '@/lib/player/streamLimitRetry';
import { disarm as disarmEventAutoAdvance } from '@/composables/useEventAutoAdvance';

export type PlayerAttachmentReason = 'user' | 'auto_retune_previous' | 'retry' | 'recovery';

export const usePlayerStore = defineStore('player', () => {
    /**
     * The channel the player is currently tuned to, or `null` when nothing
     * is playing. Stores the full Channel (name, logo, group, etc.) so
     * the global mini player can render labels without a second lookup.
     */
    const currentChannel = ref<Channel | null>(null);
    /**
     * The channel the Live page is FOCUSED on — distinct from `currentChannel`
     * (the actively-PLAYING channel, which is null while stopped). Set on every
     * tune and persists through `stopPlayback()` so the Live now/next + controls
     * panel (viewers, EPG, external-player button, Play/resume) stays visible
     * when nothing is streaming. Cleared only by the full-dismiss `closePlayer()`.
     */
    const selectedChannel = ref<Channel | null>(null);
    /**
     * True between the moment the user clicks a channel and the stream
     * URL is resolved (rewind sink started + first manifest available, or
     * transcode session created, etc.). Drives the player's loading
     * overlay so the user has visual feedback during the 1–6s switch
     * latency the rewind path can incur.
     */
    const isSwitching = ref<boolean>(false);
    /** Provider rejected this tune temporarily; the loading overlay owns feedback. */
    const isRetrying = ref(false);
    const retryAttempt = ref(0);
    const retryDelayMs = ref(0);
    // A successful session POST is not proof of playback. Retain the failure
    // count across media-level recovery until the current result renders.
    let streamLimitFailures = 0;
    function markPlaybackStarted(result: StreamUrlResult): void {
        if (streamResult.value === result) streamLimitFailures = 0;
    }
    /** Digits currently entered on Live; shared with the fullscreen player chrome. */
    const pendingChannelNumber = ref('');
    /** All rapid selections wait for the same outgoing session to finish closing. */
    let pendingRelease: Promise<void> = Promise.resolve();
    function releaseResult(result: StreamUrlResult): Promise<void> {
        pendingRelease = pendingRelease.then(() => result.cleanup()).catch(() => {
            /* best-effort — server sweepers reclaim regardless */
        });
        return pendingRelease;
    }
    /** Authoritative playback position for Live-page status and rewind chrome. */
    const isAtLiveEdge = ref<boolean>(true);
    const secondsBehindLive = ref<number>(0);
    /**
     * False once playback has failed terminally, until the next tune.
     *
     * `isAtLiveEdge` / `secondsBehindLive` are written by the player's own
     * position updates and are therefore STALE, not wrong, after a fatal
     * error — nothing stops updating them, they simply stop being updated. The
     * Phase 0B screenshot evidence caught what that looks like: after a
     * terminal `403`, the Live panel still read **"At live edge"** beside a
     * dead `0:00 → 0:00` scrubber. A confident status about a session that has
     * ended is worse than no status, so consumers gate the position claim on
     * this flag rather than presenting a number nobody is maintaining.
     *
     * Event-driven rather than age-based on purpose: an age would need a
     * ticking clock in the panel to stay reactive, and the fatal error is
     * already an event the shell handles.
     */
    const livePositionKnown = ref<boolean>(true);
    /**
     * True when the CURRENT tune is known to have hit the provider's
     * concurrent-stream cap (HTTP 429/458/509) — flagged either by hls.js on
     * the live stream or by a rewind/proxy limit response. Suppresses the
     * probe-driven transcode retry: opening another upstream connection
     * against a maxed account is doomed and amplifies the limit (2026-06-17
     * incident). Cleared when the user tunes again (`selectChannel`) or stops
     * (`closePlayer`).
     */
    const streamLimited = ref<boolean>(false);
    /**
     * The most recent `useStreamUrl` resolution. shallowRef — Vidstack
     * cares about the URL, not deep reactivity over the result object.
     * Lifted out of `Live.vue` so the player can be mounted at the App
     * shell and persist across route changes (mini-player UX).
     */
    const streamResult = shallowRef<StreamUrlResult | null>(null);
    /** Active playback diagnostic correlation id. Null when nothing is attached. */
    const playTraceId = ref<string | null>(null);
    /** Bounded provenance for the currently attached player instance. */
    const attachmentReason = ref<PlayerAttachmentReason | null>(null);
    /** Programme window start (epoch ms) of the active catchup playback. */
    const catchupStartMs = ref<number | null>(null);
    /** AbortController for an in-flight `useStreamUrl` call. */
    let _abortController: AbortController | null = null;
    /** Current in-flight tune request. Used to collapse route-sync echoes. */
    let _inFlightTuneKey: string | null = null;

    /**
     * Active stream mode — DERIVED from the streamResult, never set
     * independently. `streamResult` is the single source of truth for what
     * is playing; a second hand-maintained mode copy drifted from it.
     */
    const mode = computed<StreamMode | null>(() => streamResult.value?.mode ?? null);
    /**
     * True when a stream is actually mounted/playing, false when the channel is
     * merely SELECTED (info panel visible) but stopped or handed to an external
     * player. Drives the Live page's stopped-stage poster + Play affordance.
     */
    const isPlaying = computed(() => streamResult.value !== null);
    /** True iff the player is currently rendering a catchup playback. */
    const isPlayingCatchup = computed(() => mode.value === 'catchup');
    const isRewindActive = computed(() => mode.value === 'rewind');
    const isTranscoding = computed(() => mode.value === 'transcode');
    /**
     * Live-buffer-resilience — the playback registry id of the CURRENT
     * session, derived from `streamResult` (same convention as `mode` /
     * `isRewindActive`: no manual setter). `selectChannel` merges this into
     * the next tune's `replacePlaybackId` so switching channels on a capped
     * source self-steals the requester's own outgoing session instead of
     * 409ing — the backend removed the implicit same-user auto-steal in
     * favour of this explicit consent field.
     */
    const currentPlaybackId = computed<string | null>(() => streamResult.value?.playbackId ?? null);

    function setChannel(channel: Channel | null): void {
        currentChannel.value = channel;
        if (channel === null) {
            resetLivePosition();
        } else {
            // A non-null tune also (re)establishes the Live-page selection.
            // Nulling the playing channel (stop) deliberately does NOT touch
            // `selectedChannel` — only `closePlayer()` clears the selection.
            selectedChannel.value = channel;
        }
    }

    /**
     * Reset the live-position readout for the (derived) current mode.
     * Carries the side effects the retired `setMode` had: call after every
     * `streamResult` swap.
     */
    function resetLivePosition(): void {
        isAtLiveEdge.value = mode.value !== 'catchup';
        secondsBehindLive.value = 0;
        // A fresh stream is a fresh position claim: whatever went wrong last
        // time is no longer what the panel is describing.
        livePositionKnown.value = true;
    }

    function setLivePosition(atLiveEdge: boolean, behindSeconds: number): void {
        isAtLiveEdge.value = atLiveEdge;
        secondsBehindLive.value = Math.max(0, Math.round(behindSeconds));
        // Deliberately does NOT restore `livePositionKnown`, and the first
        // version did — which made the whole flag a no-op on the very state it
        // was built for (2026-09-07 review, finding #2).
        //
        // `RewindSeekBar`'s rAF loop emits `positionChange` whenever the
        // `${atLiveEdge}:${secondsBehindLive}` key CHANGES, with no gate on
        // session health, and that emit lands here. A dying session moves the
        // key: the Phase 0B evidence is a `0:00 → 0:00` scrubber, so the key
        // goes from e.g. "true:90" to "true:0" — and on a stall `currentTime`
        // freezes while `video.duration` keeps growing, moving it every
        // second. `markLivePositionUnknown()` runs synchronously in the error
        // handler; the next frame put the flag straight back.
        //
        // So "the player called setLivePosition" does not mean "the player
        // resumed reporting" — it means "the position key changed", including
        // as part of teardown. The one honest restore is a NEW STREAM
        // (`resetLivePosition`), which a successful retune performs.
    }

    /**
     * Mark the live-position readout unmaintained after a terminal playback
     * failure. Called by the shell's fatal-error handler; cleared by the next
     * `resetLivePosition` (a new stream) or the next `setLivePosition` (the
     * player started reporting again).
     */
    function markLivePositionUnknown(): void {
        livePositionKnown.value = false;
    }

    /**
     * Record that the current tune hit the provider's stream-limit cap.
     * Called by `NodecastPlayer` when hls.js surfaces a 429/458/509 on the
     * live stream. Idempotent; cleared on the next tune / stop.
     */
    function markStreamLimited(): void {
        streamLimited.value = true;
    }

    function setSwitching(next: boolean): void {
        isSwitching.value = next;
    }

    function tuneKey(channel: Channel, opts: { catchup?: CatchupRequest }): string {
        const catchup = opts.catchup
            ? `catchup:${opts.catchup.startMs}:${opts.catchup.durationMin}`
            : 'live';
        return `${channel.sourceId}:${channel.id}:${catchup}`;
    }

    function logTuneStop(requestedBy: 'user' | 'nodecast', reason: string): void {
        const trace = playTraceId.value;
        const channel = currentChannel.value;
        if (!trace || !channel) return;
        log.info('player.tune_stop', {
            playTraceId: trace,
            sourceId: channel.sourceId,
            channelId: channel.id,
            mode: mode.value,
            requestedBy,
            reason,
        });
    }

    function getLiveStreamFormat(settings: ReturnType<typeof useSettingsStore>): string {
        const streamFormat = settings.get<string>('streamFormat', 'm3u8');
        return typeof streamFormat === 'string' && streamFormat.trim() ? streamFormat : 'm3u8';
    }

    /**
     * Live-buffer-resilience — combine the session being replaced (if any)
     * with caller-supplied ids into the single `replacePlaybackId` field the
     * backend accepts, deduped and capped at 8 (the server's limit).
     * Returns `undefined` (not an empty array) when there is nothing to
     * replace, so a first tune sends no field at all — never garbage.
     */
    function mergeReplacePlaybackIds(
        current: string | null,
        extra?: string[],
    ): string[] | undefined {
        const ids = new Set<string>();
        if (current) ids.add(current);
        for (const id of extra ?? []) {
            if (id) ids.add(id);
        }
        if (ids.size === 0) return undefined;
        return Array.from(ids).slice(0, 8);
    }

    /**
     * Tune to a channel. Wraps `useStreamUrl` and writes the resolved
     * URL+mode into the store so the global `<NodecastPlayer>` mounted
     * in App.vue picks it up. Cancels and cleans up any in-flight prior
     * resolution. Safe to call rapidly (each call aborts the previous).
     */
    async function selectChannel(
        channel: Channel,
        opts: {
            catchup?: CatchupRequest;
            force?: boolean;
            /** Admin-only explicit slot takeover (busy toast "Stop & switch"). */
            takeover?: boolean;
            /**
             * Live-buffer-resilience — explicit same-user slot-conflict
             * consent, e.g. the `ownSessions[].playbackId` list surfaced by
             * a prior `409 { requiresReplace: true }`. Merged with the
             * store's own `currentPlaybackId` (the session THIS tune is
             * itself superseding) into the request's `replacePlaybackId`.
             */
            replacePlaybackIds?: string[];
            /** Explicit provenance for non-user attachments such as recovery/retry. */
            attachmentReason?: PlayerAttachmentReason;
            /** A playing stream hit a provider cap; wait before reacquiring it. */
            streamLimitRecovery?: boolean;
            /** Retry-After carried by the fatal media 509, if present. */
            streamLimitRecoveryRetryAfterSec?: number;
            /** Internal once-guard: this call IS the auto-retune — never chain another. */
            _retuneOfReleasedPrevious?: boolean;
            /**
             * X2 (ux-remediation D3) — explicit user Retry bypasses the
             * server's probe/preflight cache. Set ONLY by Live.vue's
             * user-initiated retry paths (busy/offline toast actions); a
             * first tune-in omits it so first-attempt behavior is
             * completely unchanged.
             */
            fresh?: boolean;
        } = {},
    ): Promise<void> {
        pendingChannelNumber.value = '';
        // Re-selecting the channel that is ALREADY playing is a no-op:
        // re-tuning visibly reloads the player and snaps the playhead back to
        // the live edge, discarding the user's rewind position — for zero
        // gain (the server would just hand back the same sink as `reused`).
        // Guarded on a live streamResult being present so a failed/offline
        // tune can still be retried by clicking the channel again. `force`
        // bypasses (rewind self-heal re-resolves the SAME channel on
        // purpose); a catchup request is always a real state change, as is
        // returning from catchup to live (mode !== 'catchup' check).
        if (
            !isSwitching.value
            && !opts.force
            && !opts.catchup
            && streamResult.value !== null
            && mode.value !== 'catchup'
            && currentChannel.value !== null
            && currentChannel.value.sourceId === channel.sourceId
            && currentChannel.value.id === channel.id
        ) {
            log.debug('player.tune_skipped_already_playing', {
                sourceId: channel.sourceId,
                channelId: channel.id,
            });
            return;
        }
        // A genuinely new tune attempt clears any prior stream-limit verdict
        // so the probe-driven transcode retry is re-armed for this channel.
        streamLimited.value = false;
        const key = tuneKey(channel, opts);
        if (_inFlightTuneKey === key) return;
        if (!opts.streamLimitRecovery
            || currentChannel.value?.sourceId !== channel.sourceId
            || currentChannel.value?.id !== channel.id) streamLimitFailures = 0;
        const nextAttachmentReason: PlayerAttachmentReason = opts._retuneOfReleasedPrevious
            ? 'auto_retune_previous'
            : (opts.attachmentReason ?? 'user');
        const nextPlayTraceId = createPlaybackAttemptId();
        log.setPlaybackAttemptId(nextPlayTraceId);
        const prevPlayTraceId = playTraceId.value;
        log.info('player.tune_start', {
            playTraceId: nextPlayTraceId,
            sourceId: channel.sourceId,
            channelId: channel.id,
            mode: opts.catchup ? 'catchup' : 'live',
            attachmentReason: nextAttachmentReason,
        });
        _inFlightTuneKey = key;
        _abortController?.abort();
        const ac = new AbortController();
        _abortController = ac;

        // Break before make: stop media polls and await the outgoing server
        // session's release before opening another provider connection. Rapid
        // selections share pendingRelease, so a later click cannot bypass it.
        const prevChannel = currentChannel.value;
        // Live-buffer-resilience: the session THIS tune is about to
        // supersede (if any) is captured BEFORE `setChannel`/streamResult
        // swap below — it's still the CURRENT streamResult at this point.
        // Merged with any explicit caller-supplied ids (e.g. a busy-toast
        // "Stop it & watch here" retry's `ownSessions`).
        const replacePlaybackId = mergeReplacePlaybackIds(currentPlaybackId.value, opts.replacePlaybackIds);
        setChannel(channel);
        isSwitching.value = true;
        isRetrying.value = false;
        retryAttempt.value = 0;
        retryDelayMs.value = 0;
        const outgoing = streamResult.value;
        if (outgoing) {
            log.info('player.tune_stop', {
                playTraceId: prevPlayTraceId,
                sourceId: prevChannel?.sourceId,
                channelId: prevChannel?.id,
                mode: outgoing.mode,
                requestedBy: 'nodecast',
                reason: 'channel_switch',
            });
            streamResult.value = null;
            void releaseResult(outgoing);
        }
        try {
            await pendingRelease;
            if (ac.signal.aborted) return;
            const settings = useSettingsStore();
            // Keep temporary provider limits in the tuning state. A newer
            // selection or Stop aborts both the request and any pending wait.
            const result = await resolveWithStreamLimitRetry(
                () => useStreamUrl(
                    {
                        sourceId: channel.sourceId,
                        id: channel.id,
                        streamFormat: getLiveStreamFormat(settings),
                    },
                    {
                        catchup: opts.catchup,
                        signal: ac.signal,
                        playTraceId: nextPlayTraceId,
                        takeover: opts.takeover,
                        replacePlaybackId,
                        fresh: opts.fresh,
                    },
                ),
                {
                    signal: ac.signal,
                    attemptsMade: streamLimitFailures,
                    initialError: opts.streamLimitRecovery
                        ? { code: 'stream_limit', retryAfterSec: opts.streamLimitRecoveryRetryAfterSec }
                        : undefined,
                    stillCurrent: () => _inFlightTuneKey === key,
                    onRetry: ({ attempt, delayMs, retryAfterSec }) => {
                        // The cap belongs to the account. Trying another event
                        // channel would cancel this wait and reset its budget.
                        disarmEventAutoAdvance('provider_limit');
                        isRetrying.value = true;
                        retryAttempt.value = attempt;
                        retryDelayMs.value = delayMs;
                        streamLimitFailures = attempt - 1;
                        log.info('player.tune_retry_stream_limit', {
                            playTraceId: nextPlayTraceId,
                            sourceId: channel.sourceId,
                            channelId: channel.id,
                            attempt,
                            delayMs,
                            retryAfterSec,
                        });
                    },
                },
            );
            if (ac.signal.aborted || _inFlightTuneKey !== key) {
                await result.cleanup();
                return;
            }
            playTraceId.value = nextPlayTraceId;
            attachmentReason.value = nextAttachmentReason;
            streamResult.value = result;
            catchupStartMs.value = result.mode === 'catchup' ? (opts.catchup?.startMs ?? null) : null;
            resetLivePosition();
            // Persist the last-watched channel so a page reload resumes
            // playback. URL deep-link on /live takes precedence at boot.
            const filters = useFiltersStore();
            filters.patch({
                lastChannel: { sourceId: channel.sourceId, channelId: channel.id },
            });
            log.info('player.tuned', {
                playTraceId: nextPlayTraceId,
                sourceId: channel.sourceId,
                channelId: channel.id,
                mode: result.mode,
                attachmentReason: nextAttachmentReason,
            });
        } catch (err) {
            if (ac.signal.aborted || _abortController !== ac) return;
            if (err instanceof Error && err.name === 'AbortError') return;
            if (isStreamLimitError(err)) streamLimited.value = true;
            if (_inFlightTuneKey === key) _inFlightTuneKey = null;
            const stillLatest = _abortController === ac;
            // A permanent channel failure may restore the previous channel
            // once. An exhausted account limit applies to that channel too.
            const previousReleased = err instanceof ApiError
                && !!err.body && typeof err.body === 'object'
                && (err.body as { previousReleased?: boolean }).previousReleased === true;
            if (
                (previousReleased || outgoing !== null)
                && !isStreamLimitError(err)
                && !opts._retuneOfReleasedPrevious
                && stillLatest
                && prevChannel
                && (prevChannel.sourceId !== channel.sourceId || prevChannel.id !== channel.id)
            ) {
                streamResult.value = null;
                log.info('player.auto_retune_previous', {
                    sourceId: prevChannel.sourceId,
                    channelId: prevChannel.id,
                });
                void selectChannel(prevChannel, { force: true, _retuneOfReleasedPrevious: true })
                    .catch(() => { /* surfaced by its own tune_failed path */ });
            }
            const msg = err instanceof ApiError ? err.message : (err as Error).message;
            log.warn('player.tune_failed', {
                playTraceId: nextPlayTraceId,
                sourceId: channel.sourceId,
                channelId: channel.id,
                error: msg,
            });
            throw err;
        } finally {
            // Clear the loading flag only when we are still the latest call.
            if (_abortController === ac) {
                isSwitching.value = false;
                isRetrying.value = false;
                retryAttempt.value = 0;
                retryDelayMs.value = 0;
                _abortController = null;
                _inFlightTuneKey = null;
            }
        }
    }

    /**
     * Dumb retry of the currently selected live channel with
     * `forceMode: 'transcode'`. The BACKEND picks the remedy — an
     * audio-repair restart of the shared buffer (mode stays 'rewind') or an
     * upstream copy-video transcode session — so no codec fields are sent.
     * Live never video-encodes; that policy is enforced server-side.
     */
    async function retryCurrentAsTranscode(): Promise<void> {
        const channel = currentChannel.value;
        if (!channel) return;
        if (mode.value === 'transcode') return;
        // The current tune already hit the provider's stream cap. A transcode
        // session opens ANOTHER upstream connection against the maxed account
        // — doomed, and it amplifies the limit. Skip (2026-06-17 incident).
        if (streamLimited.value) {
            log.warn('player.transcode_retry_suppressed_stream_limit', {
                sourceId: channel.sourceId,
                channelId: channel.id,
            });
            return;
        }

        const key = `${channel.sourceId}:${channel.id}:transcode-fallback`;
        if (_inFlightTuneKey === key) return;
        _inFlightTuneKey = key;
        _abortController?.abort();
        const ac = new AbortController();
        _abortController = ac;

        // Live-buffer-resilience: capture the PRE-cleanup playbackId — this
        // retry is inherently "replace my own current session", and the
        // backend's replace-consent check needs the id BEFORE the cleanup()
        // call below tears the registry entry down.
        const replacePlaybackId = mergeReplacePlaybackIds(currentPlaybackId.value);

        // Phase 4b — MAKE BEFORE BREAK. This used to `await
        // streamResult.value.cleanup()` right here, releasing the incumbent
        // before the replacement had even been requested. On 2026-09-03 that
        // turned a degraded session into a dead one twice: each forced
        // transcode paid a ten-second probe timeout and then had nothing to
        // fall back to, because the thing it was replacing was already gone.
        //
        // The incumbent now survives until a successor exists (released below,
        // on the success path, exactly as `selectChannel` does it). Consent is
        // unaffected: `replacePlaybackId` was captured above and the server's
        // steal-on-busy handles a capped source, which is the case this
        // pre-cleanup was presumably there to smooth. If the server does
        // release our previous session before failing, it says so with
        // `previousReleased` and the catch below acts on that rather than
        // assuming it.
        isSwitching.value = true;
        try {
            const settings = useSettingsStore();
            const result = await useStreamUrl(
                {
                    sourceId: channel.sourceId,
                    id: channel.id,
                    streamFormat: getLiveStreamFormat(settings),
                },
                {
                    forceMode: 'transcode',
                    signal: ac.signal,
                    playTraceId: playTraceId.value,
                    replacePlaybackId,
                },
            );
            if (ac.signal.aborted || _inFlightTuneKey !== key) {
                await result.cleanup();
                return;
            }
            const prevResult = streamResult.value;
            if (prevResult) {
                void prevResult.cleanup().catch(() => {
                    /* best-effort — server sweepers reclaim regardless */
                });
            }
            streamResult.value = result;
            attachmentReason.value = 'retry';
            catchupStartMs.value = null;
            resetLivePosition();
            log.info('player.transcode_retry_started', {
                playTraceId: playTraceId.value,
                sourceId: channel.sourceId,
                channelId: channel.id,
                mode: result.mode,
                attachmentReason: attachmentReason.value,
            });
        } catch (err) {
            // Aborted requests can still finish with an API error. Its release
            // verdict belongs to the old session, never a newer tune's result.
            if (ac.signal.aborted || _abortController !== ac) return;
            if (err instanceof Error && err.name === 'AbortError') {
                if (_inFlightTuneKey === key) _inFlightTuneKey = null;
                return;
            }
            if (_inFlightTuneKey === key) _inFlightTuneKey = null;
            const msg = err instanceof ApiError ? err.message : (err as Error).message;
            // Phase 4b — the incumbent is only discarded when it is KNOWN
            // dead. Two cases, and they are no longer conflated:
            //
            //  - the server released our previous session inside this failed
            //    tune (capped-source steal-on-busy, `previousReleased: true`):
            //    what is mounted is dead, so drop it rather than keep a dead
            //    URL polling — the 2026-09-03 behaviour, for the one case
            //    where it was actually correct;
            //  - anything else (a probe timeout, a 5xx, an offline channel):
            //    the incumbent was never touched and is still playing. Keep
            //    it. Discarding here is what turned two degraded sessions into
            //    dead ones.
            const previousReleased = err instanceof ApiError
                && !!err.body && typeof err.body === 'object'
                && (err.body as { previousReleased?: boolean }).previousReleased === true;
            log.warn('player.transcode_retry_failed', { error: msg, previousReleased });
            if (previousReleased) discardDeadStream('transcode_retry_failed');
            throw err;
        } finally {
            if (_abortController === ac) {
                isSwitching.value = false;
                _abortController = null;
                _inFlightTuneKey = null;
            }
        }
    }

    /**
     * Stop the STREAM but KEEP the channel selected. Releases the upstream
     * session (rewind sink / transcode session) and clears `streamResult` +
     * `currentChannel` so the global `<NodecastPlayer>` unmounts (no second
     * decoder, no orphaned upstream connection) — but leaves `selectedChannel`
     * intact so the Live now/next + controls panel stays on screen with a
     * Play/resume affordance. Used by the in-player Stop button and by the
     * "open in external player" handoff (which stops FE playback so the user
     * isn't running two concurrent streams).
     */
    async function stopPlayback(
        requestedBy: 'user' | 'nodecast' = 'user',
        reason = requestedBy === 'nodecast' ? 'admin_force_stop' : 'stop',
    ): Promise<void> {
        disarmEventAutoAdvance('playback_stopped');
        const prevResult = streamResult.value;
        logTuneStop(requestedBy, reason);
        streamResult.value = null;
        catchupStartMs.value = null;
        _abortController?.abort();
        _abortController = null;
        _inFlightTuneKey = null;
        // Clears the *playing* channel (global player unmounts) but preserves
        // selectedChannel — that's the whole point of stop-vs-close.
        setChannel(null);
        isSwitching.value = false;
        isRetrying.value = false;
        retryAttempt.value = 0;
        retryDelayMs.value = 0;
        streamLimited.value = false;
        streamLimitFailures = 0;
        pendingChannelNumber.value = '';
        playTraceId.value = null;
        log.setPlaybackAttemptId(null);
        attachmentReason.value = null;
        // A stop is "not watching right now", so a reload should not silently
        // auto-resume this channel — drop the resume marker (same as close).
        const filters = useFiltersStore();
        filters.patch({ lastChannel: null });
        if (prevResult) {
            await releaseResult(prevResult);
        }
    }

    /**
     * Unmount a stream result whose SERVER SIDE is already gone (2026-09-03
     * incident, plan Phase 0A: "failed recovery leaves no dead URL mounted or
     * polling").
     *
     * This function itself does not call `cleanup()`, but a DELETE still goes
     * out: nulling `streamResult` trips `<NodecastPlayer>`'s `props.streamResult`
     * watcher, whose `prev && prev !== next` branch awaits `prev.cleanup()` for
     * every replaced result. That is harmless and deliberate rather than
     * load-bearing — the only callers are paths where the session is already
     * known dead (a terminal `403` from the session URL, or a replacement flow
     * that released the incumbent before its successor failed), so the request
     * targets an identity the server has already dropped. It is idempotent, and
     * `useStreamUrl`'s cleanup swallows its own failure into a `log.debug`, so
     * the dead DELETE cannot reject, cannot warn, and cannot block the discard.
     * Do not "fix" this by adding a cleanup call here — that would double it.
     *
     * `currentChannel`/`selectedChannel` are kept: the viewer did not stop
     * watching, so the Live page keeps the channel selected with its stopped
     * stage + Play affordance, and the error surface can offer Retry. Returns
     * true when a result was actually discarded.
     */
    function discardDeadStream(reason: 'session_lost_recovery_failed' | 'transcode_retry_failed'): boolean {
        if (streamResult.value === null) return false;
        log.warn('player.dead_stream_discarded', {
            playTraceId: playTraceId.value,
            sourceId: currentChannel.value?.sourceId ?? null,
            channelId: currentChannel.value?.id ?? null,
            mode: mode.value,
            reason,
        });
        streamResult.value = null;
        catchupStartMs.value = null;
        isSwitching.value = false;
        attachmentReason.value = null;
        return true;
    }

    /** Clear a live result before its player component releases the session on unmount. */
    function releaseStreamResultOnUnmount(result: StreamUrlResult | null): boolean {
        if (!result || streamResult.value !== result) return false;
        void releaseResult(result);
        logTuneStop('nodecast', 'player_unmounted');
        streamResult.value = null;
        catchupStartMs.value = null;
        isSwitching.value = false;
        isRetrying.value = false;
        retryAttempt.value = 0;
        retryDelayMs.value = 0;
        streamLimited.value = false;
        streamLimitFailures = 0;
        pendingChannelNumber.value = '';
        playTraceId.value = null;
        log.setPlaybackAttemptId(null);
        attachmentReason.value = null;
        setChannel(null);
        return true;
    }

    /**
     * Stop playback entirely AND drop the selection. Releases the upstream
     * session (rewind sink / transcode session) and clears the store so the
     * global `<NodecastPlayer>` unmounts and the Live panel returns to its
     * empty "pick a channel" state.
     */
    async function closePlayer(): Promise<void> {
        disarmEventAutoAdvance('playback_stopped');
        // Clear the UI state FIRST so the player unmounts on the very next
        // tick — awaiting the session DELETE here kept the player on screen
        // for its full latency after the user pressed Stop. The release runs
        // in the background; cleanup() is idempotent and the server sweepers
        // back it up.
        const prevResult = streamResult.value;
        logTuneStop('user', 'close');
        streamResult.value = null;
        catchupStartMs.value = null;
        _abortController?.abort();
        _abortController = null;
        _inFlightTuneKey = null;
        setChannel(null);
        selectedChannel.value = null;
        pendingChannelNumber.value = '';
        isSwitching.value = false;
        isRetrying.value = false;
        retryAttempt.value = 0;
        retryDelayMs.value = 0;
        streamLimited.value = false;
        streamLimitFailures = 0;
        playTraceId.value = null;
        log.setPlaybackAttemptId(null);
        attachmentReason.value = null;
        // Clear the resume marker — explicit close means "stop watching",
        // so a future page reload should not auto-resume this channel.
        const filters = useFiltersStore();
        filters.patch({ lastChannel: null });
        if (prevResult) {
            await releaseResult(prevResult);
        }
    }

    return {
        currentChannel,
        selectedChannel,
        isPlaying,
        mode,
        isPlayingCatchup,
        isSwitching,
        isRetrying,
        retryAttempt,
        retryDelayMs,
        pendingChannelNumber,
        isAtLiveEdge,
        secondsBehindLive,
        livePositionKnown,
        markLivePositionUnknown,
        streamResult,
        playTraceId,
        attachmentReason,
        catchupStartMs,
        streamLimited,
        isRewindActive,
        isTranscoding,
        currentPlaybackId,
        setChannel,
        resetLivePosition,
        setLivePosition,
        setSwitching,
        markStreamLimited,
        markPlaybackStarted,
        selectChannel,
        retryCurrentAsTranscode,
        discardDeadStream,
        releaseStreamResultOnUnmount,
        stopPlayback,
        closePlayer,
    };
});
